// Lib
import { flow } from 'lodash';

// Utils
import globalLogger from '../../../logger';
import { isColorSwatch, isTask } from '../../../../common/elements/utils/elementTypeUtils';
import { isLocationCanvas } from '../../../../common/elements/utils/elementLocationUtils';
import * as pointLib from '../../../../common/maths/geometry/point';
import { getIconViewIconCentreOffset } from '../../../utils/dnd/dragPositionUtils';
import { getElementId, getMediaHeight, getMediaWidth } from '../../../../common/elements/utils/elementPropertyUtils';
import { isIconViewLike } from '../../../../common/elements/utils/elementDisplayUtils';
import { isWorkspaceSectionCanvas } from '../../../workspace/workspacePropertyUtils';
import { prop } from '../../../../common/utils/immutableHelper';

// Constants
import { NO_DRAG_OFFSET } from '../../../utils/dnd/dndConstants';
import { DEFAULT_ICON_VIEW_HEIGHT } from '../../../../common/elements/elementDisplayModeConstants';

const logger = globalLogger.createChannel('dnd.moved-offsets');

const isGrabbedElement = ({ grabbedElement, element }) => getElementId(grabbedElement) === getElementId(element);

// Icon-view rows and colour swatches render smaller in a list than on the canvas, so their drag
// preview needs repositioning.
const isRepositionedInListElement = (element) =>
    (isIconViewLike(element) || isColorSwatch(element)) && !isLocationCanvas(element);

const isIconViewLikeInListElement = (element) => isIconViewLike(element) && !isLocationCanvas(element);
const getIconViewPreviewHeight = (gridSize) => DEFAULT_ICON_VIEW_HEIGHT * gridSize;

const sortElementsByDistanceFromGrabbed =
    ({ grabbedMeasurement, measurementsMap, direction }) =>
    (elementA, elementB) => {
        const measurementA = prop(getElementId(elementA), measurementsMap);
        const measurementB = prop(getElementId(elementB), measurementsMap);

        const relativeYA = measurementA ? direction * (measurementA.y - grabbedMeasurement.y) : Number.POSITIVE_INFINITY;
        const relativeYB = measurementB ? direction * (measurementB.y - grabbedMeasurement.y) : Number.POSITIVE_INFINITY;

        return relativeYA - relativeYB;
    };

const getPreviewCentreOffset = ({ element, gridSize }) => {
    if (isColorSwatch(element)) {
        return { x: getMediaWidth(element) / 2, y: getMediaHeight(element) / 2 };
    }

    return getIconViewIconCentreOffset(gridSize);
};

const getInListOriginOffset = ({ element, gridSize, measurementsMap, unscaledGrabOffset, isSingleElementDrag }) => {
    const previewCentreOffset = getPreviewCentreOffset({ element, gridSize });

    if (isSingleElementDrag) return pointLib.difference(previewCentreOffset, pointLib.asPoint(unscaledGrabOffset));

    const originOffset = prop('originOffset', prop(getElementId(element), measurementsMap)) || NO_DRAG_OFFSET;

    return {
        x: 0,
        y: pointLib.difference(previewCentreOffset, originOffset).y,
    };
};

const getGrabbedElementOriginOffset = ({ grabbedElement, gridSize }) => {
    // A task is grabbed from a small handle on the right side
    if (isTask(grabbedElement)) return { x: -28 * gridSize, y: 0 };

    return NO_DRAG_OFFSET;
};

/**
 * This handles situations where the element needs to be repositioned because the dragged preview is not visually
 * the same as the element that was grabbed (for example, boards in a list are a different shape).
 */
const getElementUnscaledOriginOffset = ({
    grabbedElement,
    unscaledGrabOffset,
    element,
    gridSize,
    measurementsMap,
    isSingleElementDrag,
}) => {
    if (isRepositionedInListElement(element)) {
        return getInListOriginOffset({ element, gridSize, measurementsMap, unscaledGrabOffset, isSingleElementDrag });
    }

    if (isGrabbedElement({ grabbedElement, element })) {
        return getGrabbedElementOriginOffset({ grabbedElement, gridSize });
    }

    return NO_DRAG_OFFSET;
};

const getSelectedElementTranslation = ({
    grabbedElement,
    element,
    measurementsScaledPositions,
    gridSize,
    index,
    zoomScaleOnDragSource,
}) => {
    // If this element is the grabbed element, no translation is necessary
    if (isGrabbedElement({ grabbedElement, element })) {
        return NO_DRAG_OFFSET;
    }

    const selectedElementId = getElementId(element);
    const selectedElementScaledPosition = prop(selectedElementId, measurementsScaledPositions);

    const grabbedElementId = getElementId(grabbedElement);
    const grabbedElementScaledPosition = prop(grabbedElementId, measurementsScaledPositions);

    // If the elements are not in the measurements map then just offset them from each other
    if (!grabbedElementScaledPosition || !selectedElementScaledPosition) {
        const missingMeasurementIds = [];
        !grabbedElementScaledPosition && missingMeasurementIds.push(grabbedElementId);
        !selectedElementScaledPosition && missingMeasurementIds.push(selectedElementId);

        // Sensitive data audit: SAFE — Element IDs are system identifiers, not user-generated content
        logger.warn('No measurements found for ', missingMeasurementIds);

        return {
            x: 3 * gridSize * (index + 1),
            y: 3 * gridSize * (index + 1),
        };
    }

    return pointLib.reverseScale(
        zoomScaleOnDragSource,
        pointLib.reverseTranslate(grabbedElementScaledPosition, selectedElementScaledPosition),
    );
};

const getIconViewListVerticalSpacingOffset = ({
    draggedElements,
    grabbedElement,
    element,
    measurementsMap,
    gridSize,
    isSingleElementDrag,
}) => {
    if (isSingleElementDrag || isGrabbedElement({ grabbedElement, element })) {
        return 0;
    }

    if (isLocationCanvas(grabbedElement) || isLocationCanvas(element)) {
        return 0;
    }

    const grabbedElementId = getElementId(grabbedElement);
    const elementId = getElementId(element);

    const grabbedMeasurement = prop(grabbedElementId, measurementsMap);
    const elementMeasurement = prop(elementId, measurementsMap);

    if (!grabbedMeasurement || !elementMeasurement) return 0;

    const currentDeltaY = elementMeasurement.y - grabbedMeasurement.y;
    const direction = Math.sign(currentDeltaY);

    if (!direction) return 0;

    const elementsBetweenGrabbedAndCurrent = draggedElements
        .filter((draggedElement) => {
            if (isLocationCanvas(draggedElement)) return false;

            const draggedMeasurement = prop(getElementId(draggedElement), measurementsMap);

            if (!draggedMeasurement) return false;

            const relativeY = draggedMeasurement.y - grabbedMeasurement.y;
            return direction * relativeY >= 0 && direction * relativeY <= direction * currentDeltaY;
        })
        .sort(sortElementsByDistanceFromGrabbed({ grabbedMeasurement, measurementsMap, direction }));

    const minimumGap = getIconViewPreviewHeight(gridSize);

    const additionalSpacing = elementsBetweenGrabbedAndCurrent.slice(1).reduce((total, currentElement, index) => {
        const previousElement = elementsBetweenGrabbedAndCurrent[index];
        const previousMeasurement = prop(getElementId(previousElement), measurementsMap);
        const currentMeasurement = prop(getElementId(currentElement), measurementsMap);

        if (!previousMeasurement || !currentMeasurement) return total;

        const actualGap = Math.abs(currentMeasurement.y - previousMeasurement.y);
        const requiresIconViewSpacing =
            isIconViewLikeInListElement(previousElement) || isIconViewLikeInListElement(currentElement);

        if (!requiresIconViewSpacing) return total;

        return total + Math.max(minimumGap - actualGap, 0);
    }, 0);

    return direction * additionalSpacing;
};

const addOffsetToMapReducer =
    ({
        grabbedElement,
        draggedElements,
        measurementsMap,
        measurementsScaledPositions,
        gridSize,
        unscaledGrabOffset,
        zoomScaleOnDragSource,
        isSingleElementDrag,
    }) =>
    (offsetMap, element, index) => {
        const selectedElementTranslation = getSelectedElementTranslation({
            grabbedElement,
            element,
            measurementsScaledPositions,
            gridSize,
            index,
            zoomScaleOnDragSource,
        });

        // List icon previews are taller than the measured row spacing, so we add some space to avoid overlapping elements
        const iconViewSpacingOffsetY = getIconViewListVerticalSpacingOffset({
            draggedElements,
            grabbedElement,
            element,
            measurementsMap,
            gridSize,
            isSingleElementDrag,
        });

        const iconViewSpacingOffset = {
            x: 0,
            y: iconViewSpacingOffsetY,
        };

        offsetMap[getElementId(element)] = pointLib.translate(
            getElementUnscaledOriginOffset({
                grabbedElement,
                unscaledGrabOffset,
                element,
                gridSize,
                measurementsMap,
                isSingleElementDrag,
            }),
            pointLib.translate(iconViewSpacingOffset, selectedElementTranslation),
        );
        return offsetMap;
    };

/**
 * To calculate the offset between dragged elements, we would need to convert measurementsMap to be:
 * - Scaled -- because we are calculating the difference of the positions as shown on the interface
 * - Measure from the top left of the canvas scroll area
 */
const prepareMeasurementScaledPositions = ({ measurementsMap, zoomScale, zoomTranslationPx }) => {
    return Object.keys(measurementsMap).reduce((acc, elementId) => {
        const elementMeasurement = prop(elementId, measurementsMap);
        const elementUnscaledPosition = pointLib.asPoint(elementMeasurement);

        acc[elementId] = elementUnscaledPosition;
        if (isWorkspaceSectionCanvas(elementMeasurement)) {
            acc[elementId] = flow(
                pointLib.scale(zoomScale),
                pointLib.translate(zoomTranslationPx),
            )(elementUnscaledPosition);
        }

        return acc;
    }, {});
};

export default ({
    grabbedElement,
    draggedElements,
    measurementsMap,
    gridSize,
    unscaledGrabOffset,
    zoomScale,
    zoomScaleOnDragSource,
    zoomTranslationPx,
}) => {
    const measurementsScaledPositions = prepareMeasurementScaledPositions({
        measurementsMap,
        zoomScale,
        zoomTranslationPx,
    });

    const isSingleElementDrag = draggedElements.length === 1;

    return draggedElements.reduce(
        addOffsetToMapReducer({
            grabbedElement,
            draggedElements,
            measurementsMap,
            measurementsScaledPositions,
            gridSize,
            unscaledGrabOffset,
            zoomScaleOnDragSource,
            isSingleElementDrag,
        }),
        {},
    );
};
